import React, { useEffect, useRef, useState } from 'react'

// Third party
import { yupResolver } from '@hookform/resolvers/yup'
import { Autorenew, AutorenewRounded, CameraAlt } from '@mui/icons-material'
import { Controller, useForm } from 'react-hook-form'
import Select from 'react-select'
import { updateProfile } from 'firebase/auth'

// Models
import { UserModel } from '../../../../models/users/user.model'

// Services
import FirestoreService from '../../../../services/firestoreService'
import FirebaseStorageService from '../../../../services/storageService'
import UserService from '../../../../services/firebase-service/users/userService'

// Constants
import { CONST } from '../../../../const/const'
import { MESSAGES_CONST } from '../../../../const/messages-const'

// Styles
import { customStyles } from '../../../customStyles/ReactSelectCustomStyle'
import './styles/ProfileRootPageProfileTab.css'

// Redux
import { useAppDispatch, useAppSelector } from '../../../../store/hooks'
import { selectUserId } from '../../../../store/user/userSlice'

// Schemas
import { OrganizerProfileDetailSchema } from '../../../../validations'

// Helpers
import useToasterHelper from '../../../../helpers/ToasterHelper'
import { getConvertedData } from '../../../../models/interface.helper'

// Dummy Data
import { zoneList } from '../../../../fakeData/ZoneList'
import { CountryList } from '../../../../fakeData/countryList'
import { disciplineData } from '../../../../fakeData/disciplineList'

// Redux
import { RootState } from '../../../../store/store'
import { setProfileDetails } from '../../../../store/user/userSlice'

// Custom hooks
import useProfileHook from '../../../../hooks/users/competitor/profile/useProfileHook'

// Utils
import MessageHelperComp from '../../../../helpers/MessageHelper'

// Models
import { IUserInterface } from '../../../../models/users/user.interface'
import { ICountryList } from '../../../../types/competitor_types'

// Components
import { getUserFullName } from '../../../../helpers/helpers'
import FirebaseApp from '../../../../services/firebaseApp'
import PhoneInput from '../../../common/inputs/PhoneInput'
import GlobalInputComponent from '../../../events/forms/detail/Components/GlobalInputComponent'
import formFunctions from '../../../ui/form/form-functions/formFunctions'
import FormHeader from '../../../ui/form/form-header/FormHeader'
import useIntercomUpdate from '../../../../hooks/useIntercomUpdate'
import TimeLib from '../../../../lib/Time'

// Types
type IOnChangeArgs = {
  length: number
  isValid: boolean
  phoneNumber: string
  countryCode: string
}

const OrganizerProfileRootPageProfileTab = () => {
  // Hooks and vars

  const userReducer = useAppSelector((state: RootState) => state.user)

  const dispatch = useAppDispatch()
  const fileInputRef = useRef() as any
  const toastFunctions = useToasterHelper()
  const userId = useAppSelector(selectUserId)
  const errorRef = useRef({ isValid: true, message: '' })
  const phoneInputRef = useRef<HTMLDivElement | null>(null)
  const { updateUserDetails, getModelledUserDetails } = useProfileHook()
  const { updateIntercomUser } = useIntercomUpdate()

  const [saved, isSaved] = useState(true)

  const [loading, setLoading] = useState(false)
  const [imgLoading, setImgLoading] = useState(false)
  const [userData, setUserData] = useState<any>(null)

  const { profileDetails } = userReducer
  const formStyles = formFunctions.getFormStyles({ noPaddingRt: true })

  const {
    reset,
    watch,
    control,
    setError,
    register,
    setValue,
    getValues,
    handleSubmit,
    formState: { errors },
  } = useForm<IUserInterface>({
    shouldFocusError: true,
    mode: 'onChange',
    resolver: yupResolver(OrganizerProfileDetailSchema),
  })

  useEffect(() => {
    if (!userData) {
      const getUserData = async () => {
        let user_exists = false
        let user_doc = null

        while (!user_exists) {
          user_doc = await UserService.getUserInfoById(userId)
          user_exists = user_doc.exists()
        }
        setUserData(UserModel.fromFirestoreDoc(user_doc).toObject())
      }
      getUserData().then()
    }
  }, [userData, userId])

  useEffect(() => {
    if (!userData) {
      const getUserData = async () => {
        let user_exists = false
        let user_doc = null

        while (!user_exists) {
          user_doc = await UserService.getUserInfoById(userId)
          user_exists = user_doc.exists()
        }

        const user = UserModel.fromFirestoreDoc(user_doc).toObject()

        reset({
          ...user,
        })

        setValue('userFullName', getUserFullName(user))

        setUserData(user)
      }
      getUserData().then()
    }
  }, [userData, profileDetails, reset, setValue, userId])

  // Functions
  const phoneOnChange = (args: IOnChangeArgs) => {
    let { phoneNumber, countryCode } = args

    setValue('userPhoneCode', countryCode)
    setValue('userPhoneNumber', phoneNumber)
  }

  const onSubmit = async (dataForm: any) => {
    const data: any = { ...dataForm }

    if (!errorRef.current.isValid) {
      setError('userPhoneNumber', {
        message: errorRef.current.message,
      })

      phoneInputRef.current?.scrollIntoView({
        behavior: 'smooth',
        block: 'center',
      })

      return 0
    }

    setLoading(true)

    try {
      // Updating profile details
      let profileDetails = new UserModel(data).toObject()
      profileDetails = getConvertedData(profileDetails)

      const dataToThrow = getModelledUserDetails({ ...userReducer, ...profileDetails })

      if (FirebaseApp.auth.currentUser) {
        await updateProfile(FirebaseApp.auth.currentUser, {
          displayName: getUserFullName(profileDetails),
          photoURL: profileDetails.userProfilePicture ?? null,
        })
      }

      await updateUserDetails(dataToThrow)
      dispatch(setProfileDetails(profileDetails))

      // Update Intercom with the new profile data
      await updateIntercomUser(profileDetails)

      toastFunctions.success({ message: MESSAGES_CONST.PROFILE_UPDATED })
    } catch (error) {
      console.error(error, 'error')
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
    } finally {
      setLoading(false)
      isSaved(true)
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }

  const onError = () => {
    toastFunctions.error({
      message: 'Please fill all the required fields',
    })
  }

  async function updateProfilePic(event: any) {
    const currFormValues = watch()
    const files = event.target.files
    const file = files[0]
    const PATH = CONST.DATA.STORAGE.USERS.ENDORSEMENT_URL.PREFIX

    if (!file) return toastFunctions.success({ message: MESSAGES_CONST.NO_FILE_SELECTED })

    setImgLoading(true)

    try {
      const downloadUrl = await FirebaseStorageService.uploadFile(
        file,
        `${PATH}/${userId}`,
        (percent: number) => {
          if (0) console.log(percent)
        }
      )

      const newUserData = new UserModel({
        ...userData,
        ...currFormValues,
      })

      newUserData.userProfilePicture = (downloadUrl ?? '') as string

      await FirestoreService.updateItem(
        CONST.DATA.FIRESTORE.LATEST.COLLECTIONS.USERS.NAME,
        newUserData.id,
        {
          userProfilePicture: newUserData.userProfilePicture ?? null,
          userModified: TimeLib.utcTimestamp(),
        }
      )
      setUserData(newUserData)
      setValue('userProfilePicture', newUserData.userProfilePicture)
      toastFunctions.success({ message: MESSAGES_CONST.PIC_UPDATED })
    } catch (error) {
      toastFunctions.error({ message: MESSAGES_CONST.SOMETHING_WENT_WRONG })
      console.error(error, 'error')
    } finally {
      setImgLoading(false)
    }
  }

  const utils = {
    allowCharOnly: (e: any) => e.target.value,
  }

  // %%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%%
  /**
   * @param place Details got from React auto complete library
   * @info Sets from values according to the details got from third party lib
   */
  const handleLocation = (place: any | React.ChangeEvent<HTMLInputElement>) => {
    try {
      let location: any = {}

      if (place?.hasOwnProperty('key') && place.key === 'Enter') place.preventDefault()

      if (place && typeof ((place ?? {}) as any)?.target?.value === 'string') {
        reset({
          ...getValues(),
          userAddress: (place as any)?.target?.value ?? '',
        })
        setValue('userAddress', (place as any)?.target?.value)
        return
      }

      if (typeof place === 'object') {
        let data = place as any

        location = data?.address_components?.reduce((accumulator: any, currAddComponent: any) => {
          switch (true) {
            case currAddComponent?.types.includes('country'):
              accumulator.userNationality = currAddComponent?.long_name
              break
            case currAddComponent?.types.includes('administrative_area_level_1'):
              accumulator.userState = currAddComponent?.long_name
              break
          }
          return accumulator
        }, {})

        if (place.hasOwnProperty('formatted_address'))
          location.formatted_address = place.formatted_address

        if (location) {
          location.userLong = data?.geometry?.location?.lng()
          location.userLat = data?.geometry?.location?.lat()
        }

        const getCountryCode = CountryList.filter((currentCountry: ICountryList) => {
          return currentCountry.label.toLowerCase() === location?.userNationality.toLowerCase()
        })

        reset({
          ...getValues(),
          userNationality: getCountryCode.length ? getCountryCode[0].value : '',
          userAddress:
            location?.formatted_address ?? location?.userNationality ?? location?.userState,
          userState: location?.userState ?? '',
          userLong: location?.userLong ?? '',
          userLat: location?.userLat ?? '',
        })
      }
    } catch (error) {
      console.error(error, 'error')
    }
  }

  return (
    <div className="relative page-profile-tab">
      <FormHeader
        title="Profile settings"
        description="Update your profile details"
        headerButtonsContainer={
          <div className="flex items-center gap-2">
            {saved ? (
              <button
                type="button"
                onClick={() => isSaved(false)}
                className="items-center w-[150px] h-[45px] border border-SeabiscuitMainThemeColor rounded-lg shadow-sm text-nr font-[400] text-SeabiscuitMainThemeColor saveBtn"
              >
                Edit
              </button>
            ) : (
              <button
                type="button"
                disabled={loading}
                onClick={handleSubmit(onSubmit, onError)}
                className="items-center w-[150px] h-[45px] border border-SeabiscuitMainThemeColor rounded-lg shadow-sm text-nr font-[400] text-SeabiscuitMainThemeColor saveBtn"
              >
                {loading ? (
                  <AutorenewRounded fontSize="small" className="animate-spin" />
                ) : (
                  'Save changes'
                )}
              </button>
            )}
          </div>
        }
      />

      <div className={formStyles.className} style={formStyles.styles}>
        <div className="relative w-full pb-12 text-SeabiscuitDark200ThemeColor lg:!text-nr 2xl:!text-base profile_tab">
          <div className="md:grid gap-3">
            {/* Profile Picture */}
            <div className="flex flex-col md:flex-row flex-wrap md:items-center">
              <label className="mr-0 md:w-1/4 flex">
                <img
                  src="/assets/cp_icons/Image-1.svg"
                  alt="imgIcon"
                  className=" mb-2 md:mb-0 mr-2"
                />
                Company Logo
              </label>

              <div className="relative p-1 bg-[#d3daee54] rounded-full cursor-pointer flex flex-col md:flex-row  md:items-center justify-center w-[90px] h-[90px]">
                <input
                  type="file"
                  disabled={imgLoading || saved}
                  accept=".jpeg, .png, .jpg"
                  ref={fileInputRef}
                  onChange={(e: any) => updateProfilePic(e)}
                  className="UserProfilePicture absolute h-full w-full opacity-0 left-0 right-0 top-0 bottom-0 z-40 cursor-pointer"
                />
                {imgLoading ? (
                  <Autorenew className="animate-spin" />
                ) : (
                  <img
                    src={
                      !userData?.userProfilePicture
                        ? '/assets/og_icons/Farm-5.png'
                        : userData?.userProfilePicture
                    }
                    className="object-cover h-full w-full rounded-full"
                    alt="profile_picture"
                  />
                )}

                {!saved && (
                  <div className="bg-[#8C95AC] p-1.5 rounded-full absolute right-0 bottom-0">
                    <CameraAlt className="text-white text-xs" />
                  </div>
                )}
              </div>
            </div>

            <hr className="my-4"></hr>
            {/* User Personal Details */}

            <div className="flex flex-wrap items-center">
              <label className="w-full md:w-1/4 flex items-center md:mr-2 my-3 md:my-0">
                <img src="/assets/cp_icons/Farm-1.svg" alt="userIcons" className="mr-2" />
                Organization name
              </label>
              <div className="w-full md:w-2/5 md:min-w-[520px] md:max-w-[520px] mt-3 md:mt-0">
                <input
                  type="text"
                  {...register('userFullName', {
                    onChange: (e) => utils.allowCharOnly(e),
                  })}
                  disabled={saved}
                  placeholder="Enter name of organization..."
                  className={` w-full border rounded-xl !ring-0 p-2.5 mr-2 h-[51px] ${saved ? '!border-white text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor' : '!border-[#D3DAEE]'}`}
                />
                {errors?.userName?.message && (
                  <MessageHelperComp isError={true} message={errors.userName.message} />
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-start">
              <label className="w-full md:w-1/4 flex md:items-center mr-2 pt-3.5">
                <img src="/assets/cp_icons/Farm-1.svg" alt="userIcons" className="mr-2" />
                Organization summary
              </label>
              <div className="w-full md:w-2/5 md:min-w-[520px] md:max-w-[520px]">
                <textarea
                  key={'123'}
                  rows={4}
                  {...register('userSummary', {})}
                  disabled={saved}
                  placeholder="Add an 'About' for this organization..."
                  className={`w-full my-3 md:my-0 border !border-[#D3DAEE] placeholder:text-nr h-auto placeholder:text-[#122B4680] rounded-xl focus:ring-[#D3DAEE] p-3.5 md:min-h-[200px] lg:text-nr !ring-0 ${saved ? '!border-white text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor' : '!border-[#D3DAEE]'}`}
                />
                {errors?.userSummary?.message && (
                  <MessageHelperComp isError={true} message={errors.userSummary.message} />
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center">
              <label className="w-full md:w-1/4 flex items-center md:mr-2 my-3 md:my-0">
                <img src="/assets/og_icons/Location-1.svg" className="w-6 h-6 mr-2" alt="address" />
                Address
              </label>
              <div className="w-full md:w-2/4 md:min-w-[520px] md:max-w-[520px] mt-3 md:mt-0">
                <GlobalInputComponent
                  type="location"
                  control={control}
                  disabled={saved}
                  placeholder="Business address"
                  onChange={handleLocation}
                  value={watch('userAddress')}
                  className={`bg-[#0000] rounded-xl !py-[7px] border pl-0 ${saved ? '!border-white text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor' : '!border-[#D3DAEE]'}`}
                  inputName="userAddress"
                  register={register}
                />

                {errors?.userAddress?.message && (
                  <MessageHelperComp isError={true} message={errors.userAddress.message} />
                )}
              </div>
            </div>

            <div className="flex flex-wrap items-center ">
              <label className="w-full md:w-1/4 flex items-center md:mr-2 my-3 md:my-0">
                <img src="/assets/og_icons/Globe-1.svg" className="w-6 h-6 mr-2" alt="country" />
                Country
              </label>

              <Controller
                control={control}
                name="userNationality"
                render={({ field: { onChange, onBlur, value } }) => {
                  return (
                    <Select
                      onChange={onChange}
                      onBlur={onBlur}
                      value={CountryList.find((c) => c.value === ((value as any)?.value ?? value))}
                      options={CountryList}
                      isDisabled={saved}
                      placeholder="Select your nationality..."
                      className={` border border-solid w-[520px] selectWithSearch rounded-xl focus:ring-[#D3DAEE] focus:border-[#D3DAEE] h-[51px] ${saved ? '!border-white text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor' : '!border-[#D3DAEE]'}`}
                      styles={customStyles as any}
                      isClearable={false}
                      isMulti={false}
                      isSearchable={true}
                    />
                  )
                }}
              />
              {(errors as any)?.userNationality?.message &&
                typeof (errors as any)?.userNationality?.message === 'string' && (
                  <MessageHelperComp
                    isError={true}
                    message={(errors.userNationality as any).message}
                  />
                )}
            </div>

            <div className="flex flex-wrap items-center">
              <label className="w-full md:w-1/4 flex md:items-center md:mr-2 my-3 md:my-0">
                <img src="/assets/cp_icons/Phone.svg" className="w-7 h-7 mr-2" alt="phone" />
                Phone number
              </label>
              <div className="w-full md:w-2/4  md:min-w-[520px] md:max-w-[520px]">
                <PhoneInput
                  ref={phoneInputRef}
                  name="userPhoneNumber"
                  saved={saved}
                  onChange={phoneOnChange}
                  countryCode={watch('userPhoneCode') ?? ''}
                  phoneNumber={
                    typeof watch('userPhoneNumber') === 'object'
                      ? ''
                      : (watch('userPhoneNumber')?.replace(/[() -]/g, '') ?? '')
                  }
                />

                {errors?.userPhoneNumber?.message && (
                  <MessageHelperComp isError={true} message={errors?.userPhoneNumber?.message} />
                )}
              </div>
            </div>

            <hr className="my-4"></hr>

            {/* Zone Details */}

            <div className="flex flex-wrap items-center ">
              <label className="w-full md:w-1/4 flex items-center md:mr-2">
                {' '}
                <img className="mr-2" src="/assets/og_icons/YearofHorse-1.svg" alt="horse" />
                Discipline
              </label>

              <Controller
                control={control}
                name="userDiscipline"
                render={({ field: { onChange, onBlur, value } }) => (
                  <Select
                    onChange={onChange}
                    value={disciplineData.find((c) => c.value === ((value as any)?.value ?? value))}
                    options={disciplineData}
                    onBlur={onBlur}
                    isDisabled={saved}
                    placeholder="Select your discipline"
                    className={`my-3 md:my-0 border border-solid w-[520px] selectWithSearch rounded-xl h-[51px] ${saved ? '!border-white text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor' : '!border-[#D3DAEE]'}`}
                    styles={customStyles as any}
                    isClearable={false}
                    isMulti={false}
                    isSearchable={true}
                  />
                )}
              />
              {(errors as any)?.userDiscipline?.message &&
                typeof (errors as any)?.userNationality?.message === 'string' && (
                  <MessageHelperComp
                    isError={true}
                    message={(errors.userDiscipline as any).message}
                  />
                )}
            </div>

            <div className="flex flex-wrap items-center">
              <label className="w-full md:w-1/4 flex items-center md:mr-2 my-3 md:my-0">
                <img className="mr-2" src="/assets/og_icons/Mind Map-1.svg" alt="mind_map" />
                Zone
              </label>
              <Controller
                control={control}
                name="userZone"
                render={({ field: { onChange, onBlur, value, ref } }) => (
                  <Select
                    onChange={onChange}
                    value={zoneList.find((c) => c.value === ((value as any)?.value ?? value))}
                    options={zoneList}
                    onBlur={onBlur}
                    ref={ref}
                    isDisabled={saved}
                    placeholder="Select your zone"
                    className={`border-[#D3DAEE] border border-solid w-[520px] selectWithSearch rounded-xl focus:ring-[#D3DAEE] focus:border-[#D3DAEE] h-[51px] ${saved ? '!border-white text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor' : '!border-[#D3DAEE]'}`}
                    styles={customStyles as any}
                    isClearable={false}
                    isMulti={false}
                    isSearchable={true}
                  />
                )}
              />
              {(errors as any)?.userZone?.message &&
                typeof (errors as any)?.userNationality?.message === 'string' && (
                  <MessageHelperComp isError={true} message={(errors.userZone as any).message} />
                )}
            </div>

            <hr className="my-4"></hr>

            {/* Website */}
            <div className="flex flex-wrap items-center gap-3 md:gap-0 mb-3 md:mb-0">
              <label className="w-full md:w-1/4 flex items-center md:mr-2">
                <img src="/assets/cp_icons/Globe-1.svg" alt="userIcons" className="mr-2" />
                Website
              </label>
              <div className="w-full md:w-2/4 md:min-w-[520px] md:max-w-[520px] mt-3 md:mt-0">
                <input
                  type="text"
                  {...register('userWebsite')}
                  disabled={saved}
                  placeholder="https://www...."
                  className={`w-full border !border-[#D3DAEE] placeholder:text-SeabiscuitDark200ThemeColor rounded-xl !ring-0 p-2.5 mr-2 h-[51px] ${saved ? '!border-white text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor' : '!border-[#D3DAEE]'}}`}
                />
                {errors?.userWebsite?.message && (
                  <MessageHelperComp isError={true} message={errors.userWebsite.message} />
                )}
              </div>
            </div>

            {/* Facebook */}
            <div className="flex flex-wrap items-center gap-3 md:gap-0 mb-3 md:mb-0">
              <label className="w-full md:w-1/4 flex items-center md:mr-2">
                <img src="/assets/img/Facebook.png" alt="userIcons" className="mr-2 max-w-[24px]" />
                Facebook
              </label>
              <div className="w-full md:w-2/4  md:min-w-[520px] md:max-w-[520px]">
                <input
                  type="url"
                  {...register('userFacebook')}
                  disabled={saved}
                  placeholder="https://www.facebook.com/..."
                  className={`w-full border !border-[#D3DAEE] rounded-xl placeholder:text-SeabiscuitDark200ThemeColor !ring-0 p-2.5 mr-2 h-[51px] ${saved ? '!border-white text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor' : '!border-[#D3DAEE]'}}`}
                />
                {errors?.userFacebook?.message && (
                  <MessageHelperComp isError={true} message={errors.userFacebook.message} />
                )}
              </div>
            </div>

            {/* Instagram */}
            <div className="flex flex-wrap items-center gap-3 md:gap-0 mb-3 md:mb-0">
              <label className="w-full md:w-1/4 flex items-center md:mr-2">
                <img
                  src="/assets/img/Instagram.png"
                  alt="userIcons"
                  className="mr-2 max-w-[24px]"
                />
                Instagram
              </label>
              <div className="w-full md:w-2/4  md:min-w-[520px] md:max-w-[520px]">
                <input
                  type="text"
                  {...register('userInstagram')}
                  disabled={saved}
                  placeholder="https://www.instagram.com/..."
                  className={`w-full border !border-[#D3DAEE] rounded-xl placeholder:text-SeabiscuitDark200ThemeColor !ring-0 p-2.5 mr-2 h-[51px] ${saved ? '!border-white text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor' : '!border-[#D3DAEE]'}}`}
                />
                {errors?.userInstagram?.message && (
                  <MessageHelperComp isError={true} message={errors.userInstagram.message} />
                )}
              </div>
            </div>

            {/* Twitter */}
            <div className="flex flex-wrap items-center gap-3 md:gap-0 mb-3 md:mb-0">
              <label className="w-full md:w-1/4 flex items-center md:mr-2">
                <img src="/assets/img/x.svg" alt="userIcons" className="mr-2 max-w-[24px]" />
                Twitter
              </label>
              <div className="w-full md:w-2/4 md:min-w-[520px] md:max-w-[520px]">
                <input
                  type="text"
                  {...register('userTwitter')}
                  disabled={saved}
                  placeholder="https://www.twitter.com/..."
                  className={`w-full border !border-[#D3DAEE] rounded-xl placeholder:text-SeabiscuitDark200ThemeColor !ring-0 p-2.5 mr-2 h-[51px] ${saved ? '!border-white text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor' : '!border-[#D3DAEE]'}}`}
                />
                {errors?.userTwitter?.message && (
                  <MessageHelperComp isError={true} message={errors.userTwitter.message} />
                )}
              </div>
            </div>

            {/* LinkedIn */}
            <div className="flex flex-wrap items-center gap-3 md:gap-0 mb-3 md:mb-0">
              <label className="w-full md:w-1/4 flex items-center md:mr-2">
                <img
                  src="/assets/og_icons/LinkedIn-1.svg"
                  alt="userIcons"
                  className="mr-2 max-w-[24px]"
                />
                LinkedIn
              </label>
              <div className="w-full md:w-2/40  md:min-w-[520px] md:max-w-[520px]">
                <input
                  type="text"
                  {...register('userLinkedin')}
                  disabled={saved}
                  placeholder="https://www.linkedin.com/..."
                  className={`w-full border !border-[#D3DAEE] rounded-xl placeholder:text-SeabiscuitDark200ThemeColor !ring-0 p-2.5 mr-2 h-[51px] ${saved ? '!border-white text-SeabiscuitDark200ThemeColor placeholder:text-SeabiscuitDark200ThemeColor' : '!border-[#D3DAEE]'}}`}
                />
                {errors?.userLinkedin?.message && (
                  <MessageHelperComp isError={true} message={errors.userLinkedin.message} />
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  )
}

export default OrganizerProfileRootPageProfileTab
